import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'

/**
 * Theme and density.
 *
 * Both are attributes on `<html>`, not React state that renders classes: `index.css` declares its
 * tokens under `[data-theme='dark']` and `[data-density='dense']`, so switching either is one
 * attribute write and the whole tree re-paints from the cascade. Nothing re-renders.
 *
 * The initial value is applied by an inline script in `index.html`, before first paint. Reading
 * it here first would show one frame of the light theme on a dark reload, which is the flash
 * every console gets wrong.
 */

export type Theme = 'light' | 'dark'
export type Density = 'comfortable' | 'dense'

const THEME_KEY = 'mailroom.theme'
const DENSITY_KEY = 'mailroom.density'

interface Chrome {
  theme: Theme
  density: Density
  toggleTheme: () => void
  toggleDensity: () => void
}

const ChromeContext = createContext<Chrome>({
  theme: 'light',
  density: 'comfortable',
  toggleTheme: () => {},
  toggleDensity: () => {},
})

function read<T extends string>(key: string, fallback: T): T {
  try {
    return (localStorage.getItem(key) as T | null) ?? fallback
  } catch {
    return fallback
  }
}

export function ChromeProvider({ children }: { children: ReactNode }) {
  // Seeded from the DOM, which the pre-paint script has already set — never from a default that
  // would disagree with what is on screen.
  const [theme, setTheme] = useState<Theme>(
    () => (document.documentElement.dataset.theme as Theme) || read(THEME_KEY, 'light'),
  )
  const [density, setDensity] = useState<Density>(
    () => (document.documentElement.dataset.density as Density) || read(DENSITY_KEY, 'comfortable'),
  )

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    try {
      localStorage.setItem(THEME_KEY, theme)
    } catch {
      /* private mode; the preference is not worth failing over */
    }
  }, [theme])

  useEffect(() => {
    document.documentElement.dataset.density = density
    try {
      localStorage.setItem(DENSITY_KEY, density)
    } catch {
      /* as above */
    }
  }, [density])

  const toggleTheme = useCallback(() => setTheme((t) => (t === 'dark' ? 'light' : 'dark')), [])
  const toggleDensity = useCallback(
    () => setDensity((d) => (d === 'dense' ? 'comfortable' : 'dense')),
    [],
  )

  return (
    <ChromeContext value={{ theme, density, toggleTheme, toggleDensity }}>{children}</ChromeContext>
  )
}

export function useChrome() {
  return useContext(ChromeContext)
}
